import { useEffect, useRef, useState } from 'react';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GeoJsonLayer } from '@deck.gl/layers';
import { bbox } from '@turf/bbox';
import config from '../../config';
import * as service from '../../services/admin-service';
import { useAlerts } from '../../hooks/use-alerts';
import { Spinner } from '../../components/Spinner';

export function Hexbins() {
  const [aaid, setAaid] = useState('');
  const [result, setResult] = useState(null);
  const [isWorking, setWorking] = useState(false);
  const { showError } = useAlerts();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!aaid.trim()) return;

    setWorking(true);
    setResult(null);

    try {
      const response = await service.getHexbins(aaid.trim());
      setResult(response);
    } catch (err) {
      showError(err);
    } finally {
      setWorking(false);
    }
  }

  return (
    <Box
      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', gap: 2, mb: 2 }}
      >
        <TextField
          label="AAID"
          size="small"
          value={aaid}
          onChange={(e) => setAaid(e.target.value)}
          sx={{ width: 450 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={isWorking || !aaid.trim()}
        >
          {isWorking ? <CircularProgress size={24} /> : 'Build'}
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <Wrapper
          apiKey={config.googleMapsApiKey}
          render={(status) => {
            if (status === Status.LOADING) return <Spinner />;
            if (status === Status.FAILURE)
              return <h3>Unable to load the map</h3>;
            return <HexbinMap geojson={result} />;
          }}
        />
      </Box>
    </Box>
  );
}

function HexbinMap({ geojson }) {
  const mapRef = useRef();
  const mapInstanceRef = useRef();
  const overlayRef = useRef();

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      mapTypeId: 'hybrid',
      center: { lat: 39, lng: -98 },
      zoom: 4,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      tilt: 0,
    });

    overlayRef.current = new GoogleMapsOverlay({ getTooltip: buildTooltip });
    overlayRef.current.setMap(mapInstanceRef.current);
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !geojson?.features?.length) return;

    const [west, south, east, north] = bbox(geojson);
    mapInstanceRef.current.fitBounds({ west, south, east, north });

    const layers = [
      new GeoJsonLayer({
        id: 'hexbins',
        data: geojson,
        filled: true,
        stroked: true,
        getFillColor: [98, 151, 73, 60],
        getLineColor: [98, 151, 73, 180],
        getLineWidth: 1,
        lineWidthUnits: 'pixels',
        pickable: true,
      }),
    ];

    if (geojson.boundary) {
      layers.push(
        new GeoJsonLayer({
          id: 'boundary',
          data: {
            type: 'Feature',
            geometry: geojson.boundary,
            properties: {},
          },
          filled: false,
          stroked: true,
          getLineColor: [98, 151, 73, 200],
          getLineWidth: 2,
          lineWidthUnits: 'pixels',
        }),
      );
    }

    overlayRef.current.setProps({ layers });
  }, [geojson]);

  return <Box ref={mapRef} sx={{ height: '100%', borderRadius: 1 }} />;
}

function buildTooltip({ object }) {
  if (!object) return null;

  const YIELD_KEYS = {
    CornYields: 'Corn',
    SoyYields: 'Soy',
    WheatYields: 'Wheat',
  };

  const LABELS = {
    GridID: 'Cell',
    X: 'Longitude',
    Y: 'Latitude',
    IntersectsBoundary: 'Intersects Boundary',
    IsIrrigated: 'Is Irrigated',
    Elevation: 'Elevation',
    RelativeElevation: 'Relative Elevation',
    RelativeElevMin: 'Relative Elev Min',
    Accumulation: 'Accumulation',
    SWI: 'SWI',
    Slope: 'Slope',
    PotentialProductivity: 'Potential Productivity',
    NitrogenLoss: 'Nitrogen Loss',
    OMNSupply: 'OM N Supply',
    CEC: 'CEC',
    SSURGO_CEC: 'SSURGO CEC',
    K: 'K',
    OM: 'OM',
    SSURGO_OM: 'SSURGO OM',
    PBray: 'P',
    PCrit: 'P Critical',
    SoilPH: 'pH',
    BufPH: 'Buffer pH',
  };

  const props = object.properties;
  const yields = collectYields(props);

  const rows = Object.keys(LABELS)
    .filter((k) => k in props)
    .map(
      (k, i) =>
        `<tr style="background:${i % 2 ? '#e0e0e0' : 'white'}">
           <td style="vertical-align:top;padding:2px 8px"><b>${LABELS[k]}</b></td>
           <td style="padding:2px 8px">${formatNumber(props[k], k)}</td>
         </tr>`,
    );

  if (yields.length) {
    const bg = rows.length % 2 ? '#e0e0e0' : 'white';
    rows.push(
      `<tr style="background:${bg}">
         <td style="vertical-align:top;padding:2px 8px"><b>Yields</b></td>
         <td style="padding:2px 8px">${formatYields(yields)}</td>
       </tr>`,
    );
  }

  return {
    html: `<table style="font-family:monospace;font-size:12px;color:#111;border-collapse:collapse">${rows.join('')}</table>`,
    style: {
      background: 'white',
      borderRadius: '4px',
      padding: '0',
      margin: '0',
    },
  };

  function collectYields(properties) {
    return Object.entries(YIELD_KEYS)
      .flatMap(([key, crop]) =>
        (properties[key] ?? []).map((y) => ({ ...y, crop })),
      )
      .sort((a, b) => b.YieldYear - a.YieldYear);
  }

  function formatYields() {
    return yields
      .map(
        (y) =>
          `20${y.YieldYear}: ${y.crop} ${Number(y.Yield).toFixed(0)} bu (${Number(y.Wet).toFixed(0)}% wet)`,
      )
      .join('<br/>');
  }

  function formatNumber(value, key) {
    const precisions = { X: 6, Y: 6 };

    if (typeof value === 'number' && !Number.isInteger(value)) {
      return Number(value.toFixed(precisions[key] ?? 2));
    }

    return value;
  }
}
